import { useParams, useNavigate } from "react-router";
import {
  Package,
  MapPin,
  Clock,
  Truck,
  CheckCircle2,
  AlertCircle,
  Phone,
  Loader2,
  ExternalLink,
  ArrowLeft,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDescription,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { cn } from "@/lib/utils";
import { useTracking } from "@/api/tracking";
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Types
type ViewMode = "customer" | "admin";

// Customer-facing statuses (simplified view) - excludes internal/task-level events
const CUSTOMER_VISIBLE_STATUSES = new Set([
  "CREATED",
  "LABEL_READY",
  "LABEL_CREATED",
  "PICKED_UP",
  "IN_TRANSIT",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "DELIVERY_ATTEMPTED",
  "RETURNED",
  "CANCELLED",
  "EXCEPTION",
  "AT_FACILITY",
  "CUSTOMS_CLEARED",
]);

// Human-readable status labels for customer view
const CUSTOMER_STATUS_LABELS: Record<string, string> = {
  CREATED: "Order Received",
  LABEL_READY: "Ready for Pickup",
  LABEL_CREATED: "Ready for Pickup",
  PICKED_UP: "Picked Up",
  IN_TRANSIT: "In Transit",
  AT_FACILITY: "At Sorting Facility",
  CUSTOMS_CLEARED: "Cleared Customs",
  OUT_FOR_DELIVERY: "Out for Delivery",
  DELIVERY_ATTEMPTED: "Delivery Attempted",
  DELIVERED: "Delivered",
  RETURNED: "Returned",
  CANCELLED: "Cancelled",
  EXCEPTION: "Delivery Issue",
};

// Check if event should be visible in customer view
const isCustomerVisibleEvent = (status: string): boolean => {
  // Exclude all TASK_* events (internal operations)
  if (status.startsWith("TASK_")) return false;
  // Exclude estimated delivery (it's metadata, not an event)
  if (status === "ESTIMATED_DELIVERY") return false;
  // Include if in customer visible set
  return CUSTOMER_VISIBLE_STATUSES.has(status);
};

// Get customer-friendly label for status
const getCustomerStatusLabel = (status: string): string => {
  return CUSTOMER_STATUS_LABELS[status] || status.replace(/_/g, " ");
};
interface StatusConfig {
  color: string;
  icon: LucideIcon;
  bgColor: string;
  textColor: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

// Constants
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const CONTACT_INFO = {
  phone: "647-931-7960",
  phoneLabel: "647-931-7960 (English, Chinese)",
  email: "ezexpress.canada@gmail.com",
  hours: "Monday - Sunday: 12AM - 10PM EST",
} as const;

// Helper Functions
const getStatusConfig = (status: string): StatusConfig => {
  const configs: Record<string, StatusConfig> = {
    DELIVERED: {
      color: "text-green-600",
      bgColor: "bg-green-50",
      icon: CheckCircle2,
      textColor: "text-green-700",
    },
    OUT_FOR_DELIVERY: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    IN_TRANSIT: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    PICKED_UP: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: CheckCircle2,
      textColor: "text-gray-900",
    },
    LABEL_READY: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    CREATED: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    CANCELLED: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },
    default: {
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
  };

  return configs[status] ?? configs.default;
};

const formatTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString(undefined, DATE_FORMAT_OPTIONS);
};

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "What should I do if I can't find my package?",
    answer:
      "If your package shows as delivered but you can't find it, check with neighbors, building management, or other household members. Contact our support team if you still can't locate it.",
  },
  {
    question: "Where can I find my tracking number?",
    answer:
      "Your tracking number is included in your shipping confirmation email. You can also find it in your order details on our platform.",
  },
  {
    question: "Can I change the delivery date?",
    answer:
      "Delivery date changes depend on the shipping carrier. Contact our support team and we'll work with the carrier to accommodate your request when possible.",
  },
  {
    question: "When will I receive my package?",
    answer:
      "Estimated delivery dates are shown in your tracking details. Actual delivery times may vary based on carrier operations and local conditions.",
  },
  {
    question: "What happens if I can't receive my package in person?",
    answer:
      "Delivery procedures vary by carrier. Some may leave packages in a safe location, while others require a signature. Check your tracking updates for specific delivery instructions.",
  },
];

export function TrackingPage() {
  const { trackingNumber } = useParams<{ trackingNumber: string }>();
  const navigate = useNavigate();
  const { data: tracking, isLoading, error } = useTracking(trackingNumber);
  const [trackInput, setTrackInput] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("admin");

  const handleTrack = () => {
    if (trackInput.trim()) {
      void navigate(`/tracking/${trackInput.trim()}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !tracking) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Card className="w-full max-w-md p-8 text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-lg font-medium text-muted-foreground">
            No tracking information available
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Tracking number: {trackingNumber}
          </p>
        </Card>
      </div>
    );
  }

  const currentStatus = tracking.currentStatus || tracking.status;
  const statusConfig = getStatusConfig(currentStatus);
  const latestEvent = tracking.trackingEvents[0];

  return (
    <div className="flex flex-col">
      <BackgroundBeams />
      <div className="container mx-auto max-w-6xl relative">
        {/* Back to Admin Button */}
        <div className="py-4 px-4 md:px-0">
          <Button
            variant="ghost"
            onClick={() => navigate("/fulfillment/shipments")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Admin
          </Button>
        </div>

        <div className="gap-6 grid grid-cols-1 lg:grid-cols-3 pb-4 md:pb-8 px-4 md:px-0">
          <div className="lg:col-span-2 space-y-4 md:space-y-8">
            {/* Overview Card */}
            <Card className="overflow-hidden">
              <CardHeader className="border-b bg-muted/5 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="space-y-1 flex-1">
                    {tracking.estimatedMilestone ? (
                      <>
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          {tracking.estimatedMilestone.label}
                        </div>
                        <CardTitle
                          className={cn(
                            "text-xl sm:text-2xl font-bold tracking-tight",
                            tracking.estimatedMilestone.isComplete &&
                              "text-green-600"
                          )}
                        >
                          {tracking.estimatedMilestone.estimate ||
                            tracking.estimatedMilestone.placeholder}
                        </CardTitle>
                      </>
                    ) : (
                      <>
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          EZX Tracking Number
                        </div>
                        <CardTitle className="text-xl sm:text-2xl font-bold tracking-tight">
                          {tracking.trackingNumber}
                        </CardTitle>
                      </>
                    )}
                  </div>
                  <div
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full px-4 py-2 self-start",
                      statusConfig.bgColor
                    )}
                  >
                    <statusConfig.icon
                      className={cn("h-4 w-4", statusConfig.textColor)}
                    />
                    <span
                      className={cn(
                        "text-sm font-semibold uppercase tracking-wide",
                        statusConfig.textColor
                      )}
                    >
                      {currentStatus.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Tracking Numbers (secondary when ETA is shown) */}
                {tracking.estimatedMilestone && (
                  <div className="mb-6">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                      EZX Tracking Number
                    </div>
                    <div className="text-base font-medium">
                      {tracking.trackingNumber}
                    </div>
                    {tracking.carrierTracking?.trackingNumber && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        <span className="font-medium">Carrier:</span>{" "}
                        {tracking.carrierTracking.trackingNumber}
                      </div>
                    )}
                  </div>
                )}
                {/* Tracking Numbers when no ETA */}
                {!tracking.estimatedMilestone &&
                  tracking.carrierTracking?.trackingNumber && (
                    <div className="mb-6">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Carrier Tracking
                      </div>
                      <div className="text-base font-medium">
                        {tracking.carrierTracking.trackingNumber}
                      </div>
                    </div>
                  )}

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">
                      Latest Update
                    </div>
                    <div className="flex items-center gap-2 text-base">
                      <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium">
                        {latestEvent
                          ? formatTimestamp(latestEvent.timestamp)
                          : tracking.lastUpdated}
                      </span>
                    </div>
                  </div>
                  {latestEvent?.location && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground">
                        Current Location
                      </div>
                      <div className="flex items-center gap-2 text-base">
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium">
                          {latestEvent.location}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline Card */}
            <Card>
              <CardHeader className="border-b bg-muted/5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Tracking History</CardTitle>
                  <Tabs
                    value={viewMode}
                    onValueChange={(v) => setViewMode(v as ViewMode)}
                  >
                    <TabsList className="h-8">
                      <TabsTrigger value="customer" className="text-xs px-3">
                        Customer
                      </TabsTrigger>
                      <TabsTrigger value="admin" className="text-xs px-3">
                        Admin
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <Timeline>
                  {/* Anticipated Events (reversed so Delivered is at top) */}
                  {[...(tracking.anticipatedEvents ?? [])]
                    .reverse()
                    .map((event, index, reversedEvents) => {
                      const isLast =
                        index === reversedEvents.length - 1 &&
                        tracking.trackingEvents.length === 0;
                      const displayStatus =
                        viewMode === "customer"
                          ? getCustomerStatusLabel(event.status)
                          : event.status.replace(/_/g, " ");

                      return (
                        <TimelineItem key={`anticipated-${index}`}>
                          <TimelineSeparator>
                            <TimelineDot className="bg-transparent border-2 border-gray-300" />
                            {!isLast && (
                              <TimelineConnector className="border-l-2 border-dashed border-gray-300 bg-transparent" />
                            )}
                          </TimelineSeparator>
                          <TimelineContent>
                            <TimelineTitle className="font-medium text-gray-400">
                              {displayStatus}
                            </TimelineTitle>
                            <TimelineDescription className="text-gray-400">
                              {event.description}
                            </TimelineDescription>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })}
                  {/* Actual Events */}
                  {tracking.trackingEvents
                    .filter((event) =>
                      viewMode === "customer"
                        ? isCustomerVisibleEvent(event.status)
                        : true
                    )
                    .map((event, index, filteredEvents) => {
                      const isLast = index === filteredEvents.length - 1;
                      const config = getStatusConfig(event.status);
                      const displayStatus =
                        viewMode === "customer"
                          ? getCustomerStatusLabel(event.status)
                          : event.status.replace(/_/g, " ");

                      return (
                        <TimelineItem key={index}>
                          <TimelineSeparator>
                            <TimelineDot
                              className={cn(config.bgColor, "border-0")}
                            />
                            {!isLast && (
                              <TimelineConnector className="bg-gray-900" />
                            )}
                          </TimelineSeparator>
                          <TimelineContent>
                            <TimelineTitle
                              className={cn("font-medium", config.textColor)}
                            >
                              {displayStatus}
                            </TimelineTitle>
                            {event.description && (
                              <TimelineDescription className={config.textColor}>
                                {event.description}
                              </TimelineDescription>
                            )}
                            <TimelineDescription className={config.textColor}>
                              {formatTimestamp(event.timestamp)}
                            </TimelineDescription>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })}
                </Timeline>
                {viewMode === "customer" &&
                  tracking.trackingEvents.filter((e) =>
                    isCustomerVisibleEvent(e.status)
                  ).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No tracking updates available yet
                    </p>
                  )}
              </CardContent>
            </Card>

            {/* FAQ Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {FAQ_ITEMS.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            {/* Track Another Package Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Track Another Package</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    placeholder="Enter tracking number"
                    value={trackInput}
                    onChange={(e) => setTrackInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleTrack()}
                    className="flex-1"
                  />
                  <Button onClick={handleTrack} className="sm:w-auto">
                    Track
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Carrier Info Card - always show */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  <Truck className="h-5 w-5" />
                  {tracking.carrierTracking ? "Carrier Tracking" : "Carrier"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Carrier
                  </div>
                  <div className="font-semibold">
                    {tracking.carrierTracking?.carrierName ||
                      tracking.carrierTracking?.carrier ||
                      (tracking.metadata?.isLocalDelivery
                        ? "EZ Fleet"
                        : "Unknown")}
                  </div>
                </div>
                {tracking.carrierTracking?.trackingNumber && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Carrier Tracking Number
                    </div>
                    <div className="font-medium">
                      {tracking.carrierTracking.trackingNumber}
                    </div>
                  </div>
                )}
                {tracking.metadata?.isLocalDelivery &&
                  !tracking.carrierTracking && (
                    <div className="text-sm text-muted-foreground">
                      Local delivery handled by EZ Fleet
                    </div>
                  )}
                {tracking.carrierTracking?.carrierTrackingUrl && (
                  <a
                    href={tracking.carrierTracking.carrierTrackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Track on carrier website
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Shipping Details Card */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  <Package className="h-5 w-5" />
                  Shipping Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    From
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold">
                      {tracking.shipmentDetails.senderName || "Sender"}
                    </div>
                    {tracking.shipmentDetails.senderAddress && (
                      <div className="text-sm text-muted-foreground leading-relaxed">
                        {tracking.shipmentDetails.senderAddress}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    To
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold">
                      {tracking.shipmentDetails.recipientName || "Recipient"}
                    </div>
                    {tracking.shipmentDetails.recipientAddress && (
                      <div className="text-sm text-muted-foreground leading-relaxed">
                        {tracking.shipmentDetails.recipientAddress}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Package Details Card */}
            {tracking.packages && tracking.packages.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">
                    Package Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tracking.packages.map((pkg, index) => (
                    <div key={index} className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Package {pkg.number}
                      </div>
                      <div className="flex items-start gap-3">
                        <Package className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                        <div className="flex-1 space-y-1.5">
                          <div className="text-sm font-medium">
                            {pkg.weight} • {pkg.dimensions}
                          </div>
                          {pkg.description && (
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              {pkg.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Contact Us Card */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  <Phone className="h-5 w-5" />
                  Need Help?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Customer Service
                  </div>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="block text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {CONTACT_INFO.phoneLabel}
                  </a>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Email Support
                  </div>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="block text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline break-all"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Hours of Operation
                  </div>
                  <div className="text-sm font-medium">
                    {CONTACT_INFO.hours}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Platform Interest Card */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold leading-tight">
                  Interested in our shipping platform?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Get access to premium features, real-time tracking, and
                  competitive shipping rates for your business.
                </p>
                <Button
                  className="w-full bg-black hover:bg-black/90 text-white font-medium"
                  onClick={() => window.open("/", "_blank")}
                >
                  Learn More
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
